import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { get, noop } from 'lodash';
import styled from 'styled-components';

import { isLoadingStatus, STATUS_FAILED, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import { Row } from 'src/presentation/components/forms/Layout';
import ComplianceEventPageHeader from 'src/presentation/components/pages/compliance/components/ComplianceEventsPageHeader';
import ComplianceLogEvent, { complianceTypeToText } from 'src/actions/compliance/complianceEvent';
import Table, { EmptyStateWrapper } from 'src/presentation/components/pages/shared/Table';
import TableLayout from 'src/presentation/components/layouts/table/TableLayout';
import { toastError } from 'src/actions/toast';
import useComplianceEvents from 'src/presentation/components/pages/compliance/hooks/useComplianceEvents';
import { toDateTimeString } from '../shared/utils/TimeUtils';

const columnConfig = [
  { attr: 'content.type', label: 'Event Type' },
  {
    attr: 'description',
    label: 'Description',
    customRenderer: item => complianceTypeToText()[get(item, 'content.type')],
  },
  { attr: 'content.customerId', label: 'Customer ID' },
  { attr: 'createdAt', label: 'Date', customRenderer: item => toDateTimeString(item.createdAt) },
  { attr: 'actionByUser', label: 'Performed by' },
];

const ComplianceEvents = () => {
  const { events, loadingState } = useComplianceEvents();
  const dispatch = useDispatch();

  const [filteredItems, setFilteredItems] = useState<ComplianceLogEvent[]>([]);
  const [state, setState] = useState(loadingState);

  const EmptyState = (
    <StyledEmptyStateWrapper data-aid="emptyMessage">
      <Row>
        <span>No events found.</span>
      </Row>
    </StyledEmptyStateWrapper>
  );

  useEffect(() => {
    if (loadingState === STATUS_SUCCEEDED) {
      setFilteredItems(events);
    }
    setState(loadingState);
    if (loadingState === STATUS_FAILED) {
      dispatch(toastError('Error while loading data. Please wait and try refreshing the page'));
    }
  }, [dispatch, events, loadingState, setFilteredItems, setState]);

  const isLoading = isLoadingStatus(state);

  return (
    <TableLayout>
      <ComplianceEventPageHeader />

      <Table
        columnConfig={columnConfig}
        dataAid="events-table"
        isLoading={isLoading}
        items={filteredItems}
        renderRowActions={noop}
      />
      {!isLoading && !filteredItems.length && EmptyState}
    </TableLayout>
  );
};

const StyledEmptyStateWrapper = styled(EmptyStateWrapper)`
  height: 20vh;
`;

export default ComplianceEvents;
